import React, { useEffect, createRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Image, Box, View, Row, Spacer, DividerLine, BodyText } from 'modules/styled';
import PropTypes from 'prop-types';
import PostButtons from 'modules/post/vote-buttons/postbuttons.container';
import CommentForm from 'modules/comment/web/commentForm.component';
import { CommentBody } from 'modules/comment/commentBody';
import { layout } from 'app/styles';
import ButtonRow from 'modules/post/web/buttonRow.component';
import CommentAuthor from 'modules/comment/comment.author';
import { CommentPopup } from './comment.popup';

Comment.propTypes = {
  comment: PropTypes.object,
  activeComment: PropTypes.string,
  setActiveComment: PropTypes.func,
  parentPost: PropTypes.object,
  childComments: PropTypes.object,
  posts: PropTypes.object,
  nestingLevel: PropTypes.number,
  hidePostButtons: PropTypes.bool,
  hideReplyButtons: PropTypes.bool,
  condensedView: PropTypes.bool,
  hideBorder: PropTypes.bool,
  post: PropTypes.object,
  hideAvatar: PropTypes.bool,
  noLink: PropTypes.bool,
  avatarText: PropTypes.func,
  focusedComment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scrollTo: PropTypes.func,
  preview: PropTypes.bool,
  inMainFeed: PropTypes.bool,
  additionalNesting: PropTypes.number,
  orphan: PropTypes.bool,
};

Comment.defaultProps = {
  additionalNesting: 0,
};

function Comment(props) {
  const {
    comment,
    activeComment,
    childComments,
    posts,
    nestingLevel,
    hidePostButtons,
    hideReplyButtons,
    hideBorder,
    hideAvatar,
    noLink,
    avatarText,
    preview,
    inMainFeed,
    additionalNesting,
    parentPost,
    scrollTo,
    focusedComment,
    setActiveComment,
    orphan,
  } = props;
  const [editing, setEditing] = useState(false);
  const embeddedUser = comment?.embeddedUser;
  const hideVerticalButton = !comment?.parentPost;

  const user =
    useSelector((state) => {
      const userId = state.user.handleToId[embeddedUser?.handle];
      return state.user.users[userId];
    }) || embeddedUser;

  const screenSize = useSelector((state) => state.navigation.screenSize);

  const el = createRef();

  useEffect(() => {
    const scrollIfFocused = () => {
      if (comment && focusedComment === comment._id) {
        el?.current?.measureInWindow((x, y) => {
          scrollTo && scrollTo(0, y);
        });
      }
    };
    scrollIfFocused();
  }, [focusedComment]); // eslint-disable-line

  const cancel = () => {
    setActiveComment(null);
    setEditing(false);
  };

  if (!comment)
    return (
      <Box>
        <BodyText>[deleted]</BodyText>
        {commentChildren.map((childId) => (
          <Comment
            {...props}
            comment={posts.posts[childId]}
            key={childId}
            nestingLevel={nestingLevel + 1}
          />
        ))}
      </Box>
    );
  const isActive = activeComment === comment.id;

  const body = (
    <CommentBody
      comment={comment}
      inMainFeed={inMainFeed}
      preview={preview}
      avatarText={avatarText}
      noLink={noLink}
    />
  );

  const commentChildren = childComments?.[comment?.id] || [];
  const borderMargin =
    hidePostButtons || screenSize || !hideVerticalButton
      ? (nestingLevel && !screenSize && -3) || 0
      : layout.POST_BUTTONS_WIDTH / 3;

  const popup = <CommentPopup comment={comment} setEditing={setEditing} />;

  return (
    <Box ref={el}>
      <Spacer
        nestingLevel={nestingLevel}
        additionalNesting={additionalNesting}
        screenSize={screenSize}
      >
        <Box sx={{ m: [preview ? 0 : 2, 0], mr: [2, 3], mt: 0 }}>
          {!hideBorder && <DividerLine sx={{ height: 0.99, ml: borderMargin }} />}
          {orphan ? (
            <BodyText sx={{ mt: 1, mb: -2, ml: [0, -3] }}>[deleted]</BodyText>
          ) : null}

          <Row sx={{ mt: 4 }}>
            {!hidePostButtons && hideVerticalButton && !screenSize ? (
              <View sx={{ width: layout.POST_BUTTONS_WIDTH * 8 }}>
                <PostButtons post={comment} />
              </View>
            ) : null}
            {nestingLevel > 0 ? (
              <Image
                sx={{ height: 3, width: 2, ml: [0, -3], mr: 1 }}
                resizeMode={'contain'}
                source={require('public/img/reply.png')}
              />
            ) : null}

            <View sx={{ flex: 1 }}>
              <CommentAuthor
                user={user}
                comment={comment}
                hideAvatar={hideAvatar}
                avatarText={avatarText}
                noLink={noLink}
                popup={!preview && popup}
                preview={preview}
              />
              <Box sx={{ mt: 1 }} />
              {editing ? (
                <Box sx={{ mt: 2 }}>
                  <CommentForm
                    edit
                    sx={{ p: [2, 0], pb: [2, 4] }}
                    parentPost={parentPost}
                    comment={comment}
                    buttonText={'Update'}
                    cancel={cancel}
                    // nestingLevel={nestingLevel}
                    autoFocus
                  />
                </Box>
              ) : (
                body
              )}
              {editing || hideReplyButtons || (hidePostButtons && preview) ? null : (
                <Box sx={{ mt: 1, mb: [2, 4] }}>
                  <ButtonRow {...props} post={comment} />
                </Box>
              )}
            </View>
          </Row>
        </Box>
      </Spacer>

      {isActive && !editing && (
        <CommentForm
          parentPost={parentPost}
          nestingLevel={nestingLevel}
          sx={{ p: [2, 4] }}
          buttonText={'Comment'}
          additionalNesting={
            additionalNesting +
            (hidePostButtons || !hideVerticalButton
              ? 0
              : layout.POST_BUTTONS_NESTING_UNITS)
          }
          parentComment={comment}
          cancel={cancel}
          autoFocus
        />
      )}
      {commentChildren.map((childId) => (
        <Comment
          {...props}
          comment={posts.posts[childId]}
          key={childId}
          nestingLevel={nestingLevel + 1}
        />
      ))}
    </Box>
  );
}

export default Comment;
